import { RTSignatureSection } from "@/components/usuarios/RTSignatureSection";
import { CertificadoDigitalCard, RTArtCard } from "@/components/usuarios/RTCredentials";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Loader2, User, Building2, Save, X, Pencil, Lock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { LogoUpload, LogoAvatar } from "@/components/LogoUpload";
import { AlterarSenhaDialog } from "@/components/usuarios/AlterarSenhaDialog";
import {
  formatPhone,
  formatCpf,
  formatCnpj,
  formatCep,
} from "@/lib/formatters";
import { UF_LIST, EMPRESA_TIPO_LABELS } from "@/lib/constants";

export default function Perfil() {
  const { profile, loading: authLoading, session } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [openAlterarSenha, setOpenAlterarSenha] = useState(false);

  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [cpf, setCpf] = useState("");
  const [crea, setCrea] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [cep, setCep] = useState("");

  useEffect(() => {
    if (profile) {
      setNome(profile.nome || "");
      setCrea(profile.crea || "");
      setTelefone(profile.telefone ? formatPhone(profile.telefone) : "");
      setWhatsapp(profile.whatsapp ? formatPhone(profile.whatsapp) : "");
      setCpf(profile.cpf ? formatCpf(profile.cpf) : "");
      setLogradouro(profile.logradouro || "");
      setNumero(profile.numero || "");
      setComplemento(profile.complemento || "");
      setBairro(profile.bairro || "");
      setCidade(profile.cidade || "");
      setEstado(profile.estado || "");
      setCep(profile.cep ? formatCep(profile.cep) : "");
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PUT", `/api/usuarios/${profile!.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] });
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram salvas.",
      });
      setEditing(false);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as alterações.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      nome,
      ...(profile?.roles.includes("rt") ? { crea: crea.trim() || null } : {}),
      telefone: telefone.replace(/\D/g, "") || null,
      whatsapp: whatsapp.replace(/\D/g, "") || null,
      cpf: cpf.replace(/\D/g, "") || null,
      logradouro: logradouro || null,
      numero: numero || null,
      complemento: complemento || null,
      bairro: bairro || null,
      cidade: cidade || null,
      estado: estado || null,
      cep: cep.replace(/\D/g, "") || null,
    });
  };

  const handleCancel = () => {
    if (profile) {
      setNome(profile.nome || "");
      setCrea(profile.crea || "");
      setTelefone(profile.telefone ? formatPhone(profile.telefone) : "");
      setWhatsapp(profile.whatsapp ? formatPhone(profile.whatsapp) : "");
      setCpf(profile.cpf ? formatCpf(profile.cpf) : "");
      setLogradouro(profile.logradouro || "");
      setNumero(profile.numero || "");
      setComplemento(profile.complemento || "");
      setBairro(profile.bairro || "");
      setCidade(profile.cidade || "");
      setEstado(profile.estado || "");
      setCep(profile.cep ? formatCep(profile.cep) : "");
    }
    setEditing(false);
  };

  const displayRole = (roles: string[]) => {
    const labels: Record<string, string> = {
      owner: "CEO",
      admin: "Administrador",
      rt: "Responsável Técnico",
      tv: "Técnico de Vistoria",
      parceiro_negocios: "Parceiro de Negócio",
    };
    return roles.map((r) => labels[r] || r).join(", ");
  };

  if (authLoading) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold font-display text-slate-900 mb-6">
          Meu Perfil
        </h1>
        <div className="space-y-6 max-w-3xl">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Shell>
    );
  }

  if (!profile) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold font-display text-slate-900 mb-6">
          Meu Perfil
        </h1>
        <p className="text-muted-foreground">Perfil não encontrado.</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1
        className="text-2xl font-bold font-display text-slate-900 mb-6"
        data-testid="text-perfil-title"
      >
        Meu Perfil
      </h1>
      <div className="space-y-6 max-w-3xl">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5" />
              Informações Pessoais
            </CardTitle>
            {!editing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                data-testid="button-edit-profile"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  data-testid="button-cancel-edit"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="button-save-profile"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                {editing ? (
                  <Input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    data-testid="input-profile-nome"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-nome"
                  >
                    {profile.nome}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md text-muted-foreground"
                  data-testid="text-profile-email"
                >
                  {profile.email}
                </p>
              </div>
              <div className="space-y-2">
                <Label>CPF</Label>
                {editing ? (
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    data-testid="input-profile-cpf"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-cpf"
                  >
                    {profile.cpf ? formatCpf(profile.cpf) : "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Perfil</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md text-muted-foreground"
                  data-testid="text-profile-role"
                >
                  {displayRole(profile.roles)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                {editing ? (
                  <Input
                    value={telefone}
                    onChange={(e) => setTelefone(formatPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    data-testid="input-profile-telefone"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-telefone"
                  >
                    {profile.telefone ? formatPhone(profile.telefone) : "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                {editing ? (
                  <Input
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(formatPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    data-testid="input-profile-whatsapp"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-whatsapp"
                  >
                    {profile.whatsapp ? formatPhone(profile.whatsapp) : "—"}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {profile.roles.includes("rt") && <Card>
          <CardHeader><CardTitle className="text-lg">Credenciais do Responsável Técnico</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile-crea">CREA</Label>
              {editing ? <Input id="profile-crea" value={crea} onChange={e => setCrea(e.target.value)} /> : <p className="text-sm">{profile.crea || "Não cadastrado"}</p>}
              <p className="text-sm text-muted-foreground">Use Editar nas informações pessoais para atualizar o CREA.</p>
            </div>
            <RTSignatureSection user={profile} canManage />
            <CertificadoDigitalCard user={profile} canView canManage />
            <RTArtCard user={profile} canManage />
          </CardContent>
        </Card>}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Endereço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Logradouro</Label>
                {editing ? (
                  <Input
                    value={logradouro}
                    onChange={(e) => setLogradouro(e.target.value)}
                    placeholder="Rua, Avenida..."
                    data-testid="input-profile-logradouro"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-logradouro"
                  >
                    {profile.logradouro || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Número</Label>
                {editing ? (
                  <Input
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    placeholder="Nº"
                    data-testid="input-profile-numero"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-numero"
                  >
                    {profile.numero || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Complemento</Label>
                {editing ? (
                  <Input
                    value={complemento}
                    onChange={(e) => setComplemento(e.target.value)}
                    placeholder="Apto, Sala..."
                    data-testid="input-profile-complemento"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-complemento"
                  >
                    {profile.complemento || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Bairro</Label>
                {editing ? (
                  <Input
                    value={bairro}
                    onChange={(e) => setBairro(e.target.value)}
                    data-testid="input-profile-bairro"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-bairro"
                  >
                    {profile.bairro || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                {editing ? (
                  <Input
                    value={cidade}
                    onChange={(e) => setCidade(e.target.value)}
                    data-testid="input-profile-cidade"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-cidade"
                  >
                    {profile.cidade || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>UF</Label>
                {editing ? (
                  <select
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="select-profile-estado"
                  >
                    <option value="">Selecione</option>
                    {UF_LIST.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-estado"
                  >
                    {profile.estado || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>CEP</Label>
                {editing ? (
                  <Input
                    value={cep}
                    onChange={(e) => setCep(formatCep(e.target.value))}
                    placeholder="00000-000"
                    data-testid="input-profile-cep"
                  />
                ) : (
                  <p
                    className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                    data-testid="text-profile-cep"
                  >
                    {profile.cep ? formatCep(profile.cep) : "—"}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5" />
              Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {profile.empresa && (
              <div>
                <Label className="mb-3 block">Logo da Empresa</Label>
                {profile.roles?.includes("admin") ? (
                  <LogoUpload
                    empresaId={profile.empresa.id}
                    currentLogoUrl={profile.empresa.logoUrl}
                    session={session}
                    size="lg"
                  />
                ) : (
                  <LogoAvatar
                    empresaId={profile.empresa.id}
                    logoUrl={profile.empresa.logoUrl}
                    fallback={profile.empresa.nomeFantasia?.[0] ?? "E"}
                    size="md"
                  />
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Razão Social</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                  data-testid="text-empresa-razao"
                >
                  {profile.empresa?.razaoSocial || "—"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nome Fantasia</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                  data-testid="text-empresa-fantasia"
                >
                  {profile.empresa?.nomeFantasia || "—"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                  data-testid="text-empresa-cnpj"
                >
                  {profile.empresa?.cnpj != null
                    ? formatCnpj(profile.empresa.cnpj)
                    : "-"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <p
                  className="text-sm py-2 px-3 bg-slate-50 rounded-md"
                  data-testid="text-empresa-tipo"
                >
                  {EMPRESA_TIPO_LABELS[profile.empresa?.tipo ?? ""] ?? "—"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5" />
              Segurança
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Senha</Label>
              <p className="text-sm text-slate-600 mb-4">
                Altere sua senha para manter sua conta segura.
              </p>
              <Button
                variant="outline"
                onClick={() => setOpenAlterarSenha(true)}
              >
                <Lock className="w-4 h-4 mr-2" />
                Alterar Senha
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlterarSenhaDialog
        open={openAlterarSenha}
        onOpenChange={setOpenAlterarSenha}
        usuarioId={profile!.id}
        usuarioNome={profile!.nome}
        isOwnPassword={true}
      />
    </Shell>
  );
}
